import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Avatar,
  Chip,
  TextField,
  Card,
  CardContent,
  Drawer,
  Menu,
  MenuItem,
} from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MenuIcon from "@mui/icons-material/Menu";
import LanguageIcon from "@mui/icons-material/Language";
import { translations } from "./translations";
import { generateConsultantJsonLd, generateServiceCatalogJsonLd, injectJsonLd, updateMetaTags } from "./seo";

function App() {
  const [lang, setLang] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('lang') as 'en' | 'es') || 'en';
  });
  const [activeSection, setActiveSection] = useState<string>("home");
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  const handleLangOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchor(event.currentTarget);
  };
  const handleLangClose = () => {
    setLangAnchor(null);
  };
  const handleLangSelect = (newLang: 'en' | 'es') => {
    setLang(newLang);
    handleLangClose();
  };

  const t = translations[lang];

  useEffect(() => {
    localStorage.setItem('lang', lang);
    updateMetaTags(lang);
    
    // Inject JSON-LD
    const consultantLd = generateConsultantJsonLd(lang);
    const servicesLd = generateServiceCatalogJsonLd(t.services.items, lang);
    
    injectJsonLd(consultantLd, 'json-ld-consultant');
    injectJsonLd(servicesLd, 'json-ld-services');
  }, [lang, t]);

  const navItems = [
    { id: "home", label: t.nav.home },
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "work", label: t.nav.work },
    { id: "contact", label: t.nav.contact },
  ];

  const socialLinks = [
    {
      href: "https://www.linkedin.com/in/javier-montero-9304792b/",
      label: "LinkedIn",
      icon: <LinkedInIcon sx={{ fontSize: '2rem' }} />,
    },
  ].filter((s) => !!s.href);
  
  const skills = t.successStories.skills;

  type Experience = {
    company: string;
    role: string;
    start: string; // ISO or human
    end?: string; // 'Present' or date
    location?: string;
    industry?: string;
    summary: string[]; // paragraphs
    tech?: string[];
    logoAlt?: string;
    logoSrc?: string; // optional logo path in /public
    link?: string;
  };

  const experiences: Experience[] = t.successStories.experiences;
  
  const services = t.services.items;


  const fmtRange = (start: string, end?: string) => `${start} - ${end || "Present"}`;

  useEffect(() => {
    const sectionIds = ["home", "about", "services", "work", "booking", "contact"];
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    sections.forEach((el) => observer.observe(el));
    return () => {
      sections.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, []);

  // Compute scroll state for sleek navbar
  useEffect(() => {
    const update = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", update, { passive: true });
    return () => {
      window.removeEventListener("scroll", update);
    };
  }, []);

  const handleContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const name = (data.get("name") as string) || "";
    const email = (data.get("email") as string) || "";
    const message = (data.get("message") as string) || "";
    const targetEmail = "info@monteropalma.com";
    const subject = `Website contact from ${name}`;
    const body = `Name: ${name}\nEmail: ${email}\n\n${message}`;
    const mailto = `mailto:${encodeURIComponent(
      targetEmail
    )}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    form.reset();
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Fixed Glassmorphic Navigation Bar */}
      <AppBar
        position="fixed"
        elevation={scrolled ? 2 : 0}
        sx={{
          backgroundColor: scrolled ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.4)",
          color: "text.primary",
          transition: "all 0.3s ease",
          borderBottom: "1px solid",
          borderColor: scrolled ? "rgba(0,0,0,0.05)" : "transparent",
          backdropFilter: "saturate(180%) blur(12px)",
        }}
      >
        <Container>
          <Toolbar disableGutters sx={{ justifyContent: "space-between", height: 72 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
              JMP.
            </Typography>

            {/* Desktop Menu */}
            <Stack direction="row" spacing={2} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              {navItems.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <Button
                    key={item.id}
                    component="a"
                    href={`#${item.id}`}
                    variant="text"
                    disableRipple
                    aria-current={isActive ? "page" : undefined}
                    sx={(theme) => ({
                      px: 2,
                      fontWeight: 600,
                      borderRadius: 2,
                      fontSize: '0.95rem',
                      color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                      bgcolor: isActive ? 'rgba(15,23,42,0.06)' : 'transparent',
                      "&:hover": { 
                        bgcolor: 'rgba(15,23,42,0.08)',
                        color: theme.palette.primary.main,
                      },
                      transition: 'all 0.2s',
                    })}
                  >
                    {item.label}
                  </Button>
                );
              })}
              
              <Box sx={{ width: '1px', height: '24px', bgcolor: 'rgba(0,0,0,0.1)', mx: 1 }} />
              
               <Button
                onClick={handleLangOpen}
                startIcon={<LanguageIcon sx={{ fontSize: '1.2rem' }} />}
                sx={{ 
                  minWidth: 40, weight: 700, px: 1.5, py: 0.8, borderRadius: 2,
                  color: 'primary.main',
                  bgcolor: 'rgba(59, 130, 246, 0.08)',
                  textTransform: 'none',
                  "&:hover": { bgcolor: 'rgba(59, 130, 246, 0.15)' }
                }}
              >
                {lang === 'en' ? 'English' : 'Español'}
              </Button>
            </Stack>

            {/* Mobile Hamburger Icon */}
            <Stack direction="row" spacing={1} sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
              <IconButton
                onClick={handleLangOpen}
                sx={{ 
                  color: 'primary.main',
                  bgcolor: 'rgba(59, 130, 246, 0.08)',
                  borderRadius: 2,
                }}
              >
                <LanguageIcon />
              </IconButton>
              <IconButton 
                sx={{ color: 'text.primary' }}
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
              >
                <MenuIcon />
              </IconButton>
            </Stack>

            {/* Shared Language Menu */}
            <Menu
              anchorEl={langAnchor}
              open={Boolean(langAnchor)}
              onClose={handleLangClose}
              disableScrollLock
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }
              }}
            >
              <MenuItem 
                onClick={() => handleLangSelect('en')}
                selected={lang === 'en'}
                sx={{ fontWeight: 600, px: 3 }}
              >
                English
              </MenuItem>
              <MenuItem 
                onClick={() => handleLangSelect('es')}
                selected={lang === 'es'}
                sx={{ fontWeight: 600, px: 3 }}
              >
                Español
              </MenuItem>
            </Menu>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { width: 250, p: 3, bgcolor: '#ffffff' } }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 4, letterSpacing: '-0.03em' }}>
          JMP.
        </Typography>
        <Stack spacing={2}>
          {navItems.map((item) => {
            const isActive = activeSection === item.id;
            return (
              <Button
                key={item.id}
                component="a"
                href={`#${item.id}`}
                onClick={() => setMobileOpen(false)}
                disableRipple
                sx={(theme) => ({ 
                  justifyContent: 'flex-start', 
                  fontSize: '1.05rem', 
                  fontWeight: 600, 
                  px: 2, py: 1.5,
                  borderRadius: 2,
                  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                  bgcolor: isActive ? 'rgba(15,23,42,0.06)' : 'transparent',
                })}
              >
                {item.label}
              </Button>
            );
          })}
        </Stack>
      </Drawer>

      {/* Hero Section */}
      <Box
        id="home"
        component="section"
        sx={{
          scrollMarginTop: 80,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          position: "relative",
          pt: { xs: 12, md: 0 },
        }}
      >
        {/* Abstract Background Elements */}
        <Box sx={{ position: 'absolute', top: '-10%', left: '-10%', width: '50vw', height: '50vw',
           background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, rgba(255,255,255,0) 70%)', zIndex: 0, borderRadius: '50%' }} />
        <Box sx={{ position: 'absolute', bottom: '0%', right: '-10%', width: '60vw', height: '60vw',
           background: 'radial-gradient(circle, rgba(16,185,129,0.08) 0%, rgba(255,255,255,0) 70%)', zIndex: 0, borderRadius: '50%' }} />

        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ maxWidth: 880 }}>
             <Typography variant="h5" sx={{ color: 'secondary.main', fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {t.hero.tagline}
            </Typography>
            <Typography variant="h1" sx={{ mb: 2, color: 'text.primary', fontSize: { xs: '3rem', sm: '4.5rem', md: '5.5rem' }, lineHeight: 1.1 }}>
              {t.hero.title_top} <br/>
              <Box component="span" sx={{
                background: 'linear-gradient(90deg, #0f172a 0%, #3b82f6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
              {t.hero.title_bottom}
              </Box>
            </Typography>
            <Typography variant="h4" sx={{ mb: 4, color: 'text.secondary', fontWeight: 500, maxWidth: 650, lineHeight: 1.4, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
              {t.hero.subtitle}
            </Typography>

            <Stack direction="row" spacing={3} sx={{ mt: 5 }} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button variant="contained" size="large" href="#booking"
                sx={{ px: 4, py: 1.5, fontSize: '1.1rem', borderRadius: 50 }}>
                {t.hero.cta}
              </Button>
              <Stack direction="row" spacing={1.5}>
                {socialLinks.map((s) => (
                  <IconButton
                    key={s.label}
                    component="a"
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    aria-label={s.label}
                    sx={{
                      color: 'text.secondary',
                      bgcolor: 'rgba(15,23,42,0.04)',
                      border: '1px solid rgba(15,23,42,0.05)',
                      "&:hover": { bgcolor: '#ffffff', color: 'primary.main', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
                      transition: 'all 0.2s',
                      width: 60,
                      height: 60,
                    }}
                  >
                    {s.icon}
                  </IconButton>
                ))}
              </Stack>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* About Section */}
      <Box
        id="about"
        component="section"
        sx={{
          py: { xs: 10, md: 16 },
          scrollMarginTop: 80,
          position: "relative",
          bgcolor: '#ffffff',
          borderRadius: { xs: 0, md: '48px 48px 0 0' },
          boxShadow: '0 -10px 40px rgba(0,0,0,0.02)',
        }}
      >
        <Container>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "auto 1fr" },
              alignItems: "center",
              gap: { xs: 6, md: 10 },
            }}
          >
            <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {/* Perfectly circular decorative background matching Avatar size + padding */}
              <Box sx={{ 
                position: 'absolute', 
                width: { xs: 280, md: 380 }, 
                height: { xs: 280, md: 380 }, 
                background: 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(255,255,255,0) 100%)', 
                borderRadius: '50%', 
                zIndex: 0 
              }} />
              <Avatar
                src="/goodMe.jpg"
                alt="Javier Montero Palma"
                sx={{ 
                  width: { xs: 240, md: 320 }, 
                  height: { xs: 240, md: 320 }, 
                  boxShadow: '0 20px 40px -10px rgba(0,0,0,0.15)',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </Box>
            <Box>
              <Typography variant="h2" sx={{ mb: 3 }}>
                {t.about.title}
              </Typography>
              <Typography sx={{ mb: 3, fontSize: '1.125rem', color: 'text.secondary', lineHeight: 1.8 }}>
                {t.about.p1}
              </Typography>
              <Typography sx={{ fontSize: '1.125rem', color: 'text.secondary', lineHeight: 1.8 }}>
                {t.about.p2}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Services Section */}
      <Box
        id="services"
        component="section"
        sx={{
          py: { xs: 10, md: 16 },
          scrollMarginTop: 80,
          bgcolor: 'rgba(15, 23, 42, 0.02)',
        }}
      >
        <Container>
          <Typography variant="h2" sx={{ mb: 6, textAlign: 'center' }}>
            {t.services.title}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr 1fr" },
              gap: 4,
            }}
          >
            {services.map((service) => (
              <Card key={service.title} sx={{ height: '100%', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-8px)' } }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h3" sx={{ fontSize: '3rem', mb: 2 }}>{service.icon}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>{service.title}</Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>{service.description}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Success Stories Section */}
      <Box
        id="work"
        component="section"
        sx={{ py: { xs: 10, md: 16 }, scrollMarginTop: 80 }}
      >
        <Container>
          <Typography variant="h2" sx={{ mb: 2 }}>{t.successStories.title}</Typography>
          <Typography color="text.secondary" sx={{ mb: 8, fontSize: '1.125rem', maxWidth: 650, lineHeight: 1.7 }}>
            {t.successStories.subtitle}
          </Typography>

          {/* Skills / Tech Stack */}
          <Box sx={{ mb: 10 }}>
            <Typography variant="h4" sx={{ mb: 4 }}>{t.successStories.skillsTitle}</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {skills.map((skill) => (
                <Chip
                  key={skill}
                  label={skill}
                  variant="outlined"
                  sx={{ 
                    px: 2, py: 2.5, fontSize: '0.95rem',
                    bgcolor: 'rgba(255,255,255,0.6)',
                    "&:hover": { bgcolor: '#ffffff', borderColor: '#cbd5e1', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }
                  }}
                />
              ))}
            </Box>
          </Box>

          <Typography variant="h4" sx={{ mb: 4 }}>{t.successStories.engagementsTitle}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {experiences.map((exp) => (
              <Card key={`${exp.company}-${exp.start}`}>
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 3, md: 4 }, alignItems: 'flex-start' }}>
                    <Avatar
                      src={exp.logoSrc}
                      alt={exp.logoAlt || exp.company}
                      sx={{ 
                        width: { xs: 64, md: 80 }, height: { xs: 64, md: 80 }, 
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                        bgcolor: '#ffffff',
                        color: 'primary.main',
                        fontSize: '2rem',
                        fontWeight: 700
                      }}
                    >
                      {!exp.logoSrc && exp.company.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1, width: '100%' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h4" sx={{ color: 'text.primary', mb: 0.5, fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
                            {exp.role}
                          </Typography>
                          <Typography variant="h6" color="secondary.main" sx={{ mb: 1 }}>
                            {exp.company} {exp.industry ? `· ${exp.industry}` : ""}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ 
                          color: 'text.secondary', fontWeight: 600, py: 0.75, px: 2, 
                          bgcolor: 'rgba(15,23,42,0.04)', borderRadius: 2,
                          mt: { xs: 1, sm: 0 },
                        }}>
                          {fmtRange(exp.start, exp.end)}
                        </Typography>
                      </Box>
                      
                      {exp.summary.map((p, i) => (
                        <Typography key={i} color="text.secondary" sx={{ mb: 2, lineHeight: 1.7, fontSize: '1.05rem' }}>
                          {p}
                        </Typography>
                      ))}

                      {exp.tech && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
                          {exp.tech.map((t) => (
                            <Chip
                              key={t}
                              label={t}
                              size="small"
                              sx={{ 
                                bgcolor: 'rgba(59, 130, 246, 0.08)', 
                                color: 'primary.main', 
                                fontWeight: 600,
                                px: 0.5
                              }}
                            />
                          ))}
                        </Stack>
                      )}

                      {exp.link && (
                        <Button
                          component="a"
                          href={exp.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          size="small"
                          startIcon={<LanguageIcon sx={{ fontSize: '1.2rem' }} />}
                          sx={{ 
                            mt: 3, 
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            color: 'primary.main',
                            borderColor: 'rgba(59, 130, 246, 0.4)',
                            px: 2,
                            py: 0.8,
                            "&:hover": {
                              borderColor: 'primary.main',
                              bgcolor: 'rgba(59, 130, 246, 0.05)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.1)',
                            },
                            transition: 'all 0.2s',
                          }}
                        >
                          {lang === 'en' ? 'Visit Project' : 'Ver Proyecto'}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Booking Section */}
      <Box
        id="booking"
        component="section"
        sx={{ 
          py: { xs: 10, md: 16 }, 
          scrollMarginTop: 80,
          bgcolor: 'rgba(59, 130, 246, 0.03)',
          borderTop: '1px solid rgba(15, 23, 42, 0.05)',
          borderBottom: '1px solid rgba(15, 23, 42, 0.05)',
        }}
      >
        <Container>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              alignItems: "center",
              gap: { xs: 6, md: 10 },
            }}
          >
            <Box sx={{ order: { xs: 2, md: 1 } }}>
              <Typography variant="h2" sx={{ mb: 3 }}>
                {t.booking.title}
              </Typography>
              <Typography sx={{ mb: 4, fontSize: '1.25rem', color: 'text.secondary', lineHeight: 1.6 }}>
                {t.booking.subtitle}
              </Typography>
              <Button 
                variant="contained" 
                size="large" 
                href="https://tidycal.com/javierantoniomonteropalma/20-minute-discovery-call"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  px: 6, py: 2, fontSize: '1.2rem', borderRadius: 4,
                  background: 'linear-gradient(135deg, #0f172a 0%, #3b82f6 100%)',
                  boxShadow: '0 10px 20px -5px rgba(59, 130, 246, 0.3)',
                  "&:hover": {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 15px 30px -10px rgba(59, 130, 246, 0.4)',
                  }
                }}
              >
                {t.booking.cta}
              </Button>
            </Box>
            <Box sx={{ order: { xs: 1, md: 2 }, position: 'relative' }}>
               <Box sx={{ 
                position: 'absolute', 
                top: '5%', right: '5%', bottom: '-5%', left: '-5%',
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: 4,
                zIndex: 0
              }} />
              <Box
                component="img"
                src="/discovery_call.png"
                alt={t.booking.imgAlt}
                sx={{ 
                  width: '100%', 
                  height: 'auto', 
                  borderRadius: 4,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  position: 'relative',
                  zIndex: 1
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>


      {/* Contact Section */}
      <Box
        id="contact"
        component="section"
        sx={{ py: { xs: 10, md: 16 }, scrollMarginTop: 80, bgcolor: '#ffffff', position: 'relative' }}
      >
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ maxWidth: 650, mx: 'auto', textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t.contact.title}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}>
              {t.contact.subtitle}
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleContactSubmit}
            sx={{ maxWidth: 800, mx: 'auto' }}
          >
            <Card sx={{ bgcolor: 'rgba(248, 250, 252, 0.5)', backdropFilter: 'blur(8px)' }}>
              <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 3,
                  }}
                >
                  <TextField 
                    fullWidth label={t.contact.nameLabel} name="name" required 
                    variant="outlined"
                    sx={{ bgcolor: '#ffffff', borderRadius: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth label={t.contact.emailLabel} name="email" type="email" required
                    variant="outlined"
                    sx={{ bgcolor: '#ffffff', borderRadius: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth label={t.contact.messageLabel} name="message" multiline minRows={5} required
                    variant="outlined"
                    sx={{ gridColumn: { xs: "1", md: "1 / -1" }, bgcolor: '#ffffff', borderRadius: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" }, mt: 2 }}>
                    <Button type="submit" variant="contained" size="large" fullWidth sx={{ py: 2, fontSize: '1.1rem', borderRadius: 3 }}>
                      {t.contact.submitBtn}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ mt: 10, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {t.contact.footer.replace('{year}', new Date().getFullYear().toString())}
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default App;
